import BaseDetailView from '../shared/BaseDetailView';
import Badge from '../shared/Badge';
import NavigationArrow from '../shared/NavigationArrow';
import SearchableAccessList from '../shared/SearchableAccessList';
import DetailsTabLayout, { FieldSection, SectionDivider } from '../shared/DetailsTabLayout';
import { ICONS } from '../../constants/detailViewConstants';
import { useState, useLayoutEffect } from 'react';
import { useUIState } from '../../contexts/UIStateContext';
import { capitalizeFirstLetter } from '../../utils/textFormat';

export default function PermissionDetailView({ permission, onPrevious = null, onNext = null, theme = 'purple' }) {
  const { getAdminPermissionTab, setAdminPermissionTab } = useUIState();
  const [activeTab, setActiveTab] = useState('details');

  // Restore saved tab when permission changes
  useLayoutEffect(() => {
    if (permission?.id) {
      const savedTab = getAdminPermissionTab(permission.id);
      setActiveTab(savedTab);
    }
  }, [permission?.id, getAdminPermissionTab]);

  // Wrapper to persist active tab to UIState (per permission)
  const handleSetActiveTab = (tab) => {
    setActiveTab(tab);
    if (permission?.id) {
      setAdminPermissionTab(permission.id, tab);
    }
  };

  // Header content with icon, title, and display name
  const headerContent = (
    <div className="flex items-center space-x-4">
      {/* Navigation Arrow - Previous */}
      {(onPrevious || onNext) && (
        <NavigationArrow
          direction="previous"
          onClick={onPrevious}
          disabled={!onPrevious}
          label={onPrevious ? 'Previous permission' : 'No previous permission'}
        />
      )}

      <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--color-bg-secondary)' }}>
        <svg className="w-8 h-8" style={{ color: 'var(--color-text-muted)' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
      </div>
      <div>
        <h2 className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>{permission?.display_name || 'Permission'}</h2>
        <div className="flex items-center gap-2 mt-1">
          <Badge variant="gray">
            {permission?.users_with_permission?.length || 0} {permission?.users_with_permission?.length === 1 ? 'user' : 'users'}
          </Badge>
          <Badge variant="gray">
            {permission?.roles_with_permission?.length || 0} {permission?.roles_with_permission?.length === 1 ? 'role' : 'roles'}
          </Badge>
        </div>
      </div>

      {/* Navigation Arrow - Next */}
      {(onPrevious || onNext) && (
        <NavigationArrow
          direction="next"
          onClick={onNext}
          disabled={!onNext}
          label={onNext ? 'Next permission' : 'No next permission'}
        />
      )}
    </div>
  );

  // Custom tabs for permissions - Details, Users, Roles
  const tabs = [
    { id: 'details', label: 'Details', icon: ICONS.details },
    { id: 'users', label: 'Users', icon: ICONS.access },
    { id: 'roles', label: 'Roles', icon: ICONS.roles }
  ];

  // Render user card
  const renderUserCard = (user, hasPermission) => (
    <div
      key={user.id}
      className="rounded-lg p-4 transition-all border"
      style={{ backgroundColor: 'var(--color-bg-tertiary)', borderColor: 'var(--color-border-default)' }}
      onMouseEnter={(e) => e.currentTarget.style.borderColor = 'var(--color-gradient-via)'}
      onMouseLeave={(e) => e.currentTarget.style.borderColor = 'var(--color-border-default)'}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h4 className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>{user.username}</h4>
            {user.full_name && (
              <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>({user.full_name})</span>
            )}
          </div>
          <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>{user.email}</p>
          {hasPermission && user.permission_source && (
            <div className="mt-2">
              <Badge
                variant={user.permission_source === 'direct' ? 'direct' : 'role'}
                             >
                {user.permission_source === 'direct' ? 'Direct Permission' : 'Via Role'}
              </Badge>
              {user.role_sources && user.role_sources.length > 0 && (
                <div className="mt-1 flex flex-wrap gap-1">
                  {user.role_sources.map((roleName, idx) => (
                    <span key={idx} className="text-xs text-gray-500">
                      {roleName}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        <div className={`flex-shrink-0 w-3 h-3 rounded-full ${hasPermission ? 'bg-green-500' : 'bg-gray-600'}`} />
      </div>
    </div>
  );

  // Render role card
  const renderRoleCard = (role, hasPermission) => (
    <div
      key={role.id}
      className="rounded-lg p-4 transition-all border"
      style={{ backgroundColor: 'var(--color-bg-tertiary)', borderColor: 'var(--color-border-default)' }}
      onMouseEnter={(e) => e.currentTarget.style.borderColor = 'var(--color-gradient-via)'}
      onMouseLeave={(e) => e.currentTarget.style.borderColor = 'var(--color-border-default)'}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h4 className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>{role.name}</h4>
            <Badge variant={role.theme || 'blue'}>
              {role.theme}
            </Badge>
          </div>
          <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>{role.user_count} {role.user_count === 1 ? 'user' : 'users'}</p>
        </div>
        <div className={`flex-shrink-0 w-3 h-3 rounded-full ${hasPermission ? 'bg-green-500' : 'bg-gray-600'}`} />
      </div>
    </div>
  );

  // Render content based on active tab
  const renderContent = () => {
    if (activeTab === 'details') {
      if (!permission) {
        return (
          <div className="flex items-center justify-center h-full min-h-[400px]">
            <p style={{ color: 'var(--color-text-muted)' }}>Select a permission to view details</p>
          </div>
        );
      }
      return (
        <DetailsTabLayout
          title="Permission Details"
          subtitle="View permission information and usage"
          showEditButtons={false}
        >
          <FieldSection label="Permission Name">
            <div className="w-full h-11 bg-gray-800/50 border border-gray-700/50 rounded-lg px-4 flex items-center text-white text-sm">
              {permission.display_name}
            </div>
          </FieldSection>

          <SectionDivider />

          <FieldSection label="Description">
            <div className="w-full min-h-[44px] bg-gray-800/50 border border-gray-700/50 rounded-lg px-4 py-3 text-white text-sm">
              {permission.description || 'No description'}
            </div>
          </FieldSection>
        </DetailsTabLayout>
      );
    }

    if (activeTab === 'users') {
      if (!permission) {
        return (
          <div className="flex items-center justify-center h-full min-h-[400px]">
            <p style={{ color: 'var(--color-text-muted)' }}>Select a permission to view details</p>
          </div>
        );
      }

      return (
        <SearchableAccessList
          title="Permission Users"
          subtitle="Manage which users have this permission"
          itemsWithAccess={permission.users_with_permission || []}
          itemsWithoutAccess={permission.users_without_permission || []}
          itemType="user"
          accessLabel="Permission"
          onToggleAccess={(userId, currentHasAccess) => {
            console.log(`Toggle permission for user ${userId}, currently has: ${currentHasAccess}`);
            // TODO: Implement API call to grant/remove permission
          }}
          renderItemContent={(user, hasPermission) => (
            <div>
              <div className="flex items-center gap-2">
                <h4 className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>{capitalizeFirstLetter(user.username)}</h4>
                {user.full_name && (
                  <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>({capitalizeFirstLetter(user.full_name)})</span>
                )}
              </div>
              {/* Show user's permissions as badges */}
              <div className="mt-2">
                <div className="flex gap-1 overflow-hidden">
                  {user.permission_ids && user.permission_ids.length > 0 ? (
                    user.permission_ids.map((permissionId, idx) => (
                      <Badge key={idx} variant="gray" className="whitespace-nowrap flex-shrink-0">
                        {permissionId.replace(/_/g, ' ')}
                      </Badge>
                    ))
                  ) : (
                    <Badge variant="gray-muted" className="whitespace-nowrap flex-shrink-0">
                      No Permissions
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          )}
        />
      );
    }

    if (activeTab === 'roles') {
      if (!permission) {
        return (
          <div className="flex items-center justify-center h-full min-h-[400px]">
            <p style={{ color: 'var(--color-text-muted)' }}>Select a permission to view details</p>
          </div>
        );
      }

      return (
        <SearchableAccessList
          title="Permission Roles"
          subtitle="Manage which roles have this permission"
          itemsWithAccess={permission.roles_with_permission || []}
          itemsWithoutAccess={permission.roles_without_permission || []}
          itemType="role"
          accessLabel="Permission"
          onToggleAccess={(roleId, currentHasAccess) => {
            console.log(`Toggle permission for role ${roleId}, currently has: ${currentHasAccess}`);
            // TODO: Implement API call to grant/remove permission from role
          }}
          renderItemContent={(role, hasPermission) => (
            <div>
              <div className="flex items-center gap-2">
                <h4 className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>{capitalizeFirstLetter(role.name)}</h4>
              </div>
              {/* Show role's permissions as badges */}
              <div className="mt-2">
                <div className="flex gap-1 overflow-hidden">
                  {role.permission_ids && role.permission_ids.length > 0 ? (
                    role.permission_ids.map((permissionId, idx) => (
                      <Badge key={idx} variant="gray" className="whitespace-nowrap flex-shrink-0">
                        {permissionId.replace(/_/g, ' ')}
                      </Badge>
                    ))
                  ) : (
                    <Badge variant="gray-muted" className="whitespace-nowrap flex-shrink-0">
                      No Permissions
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          )}
        />
      );
    }

    return null;
  };

  return (
    <BaseDetailView
      item={permission}
      emptyStateIcon="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
      emptyStateTitle="No permission selected"
      emptyStateSubtitle="Select a permission from the sidebar to view details"
      headerContent={headerContent}
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={handleSetActiveTab}
      headerActions={[]}
      renderContent={renderContent}
    />
  );
}
